<template>
    <GenericTable :Headers="userHeaders" :Rows="userList" :EnablePagination="true" :SelectionType="selectionTypeEnum.Multi" :ShowCountRowNumber="true">
        <!-- Suppression de la selection d'utilisateurs -->
        <template #toolbar-right="{ SelectedRows }">
            <b-button @click="deleteUser(SelectedRows)" v-b-tooltip.noninteractive.hover :title="$t('Delete')" :variant="Variant"
                :disabled="!$store.getters['CheckAction']('Delete', undefined, Action)"
                :class="'btn-circle border-0 p-0 text-' + Variant + '-light'">
                <b-icon icon="x" />
            </b-button>
        </template>
        
        <!-- Boutons d'action sur un utilisateur situé à gauche des colonnes -->
        <template #menu-left="{ row }">
            <b-button @click="deleteUser( [row] )" v-b-tooltip.noninteractive.hover :title="$t('Delete')" variant="outline-danger" 
                :disabled="!$store.getters['CheckAction']('Delete', undefined, Action)"
                class="btn-circle border-0 p-0">
                <b-icon icon="x" />
            </b-button>
        </template> 

        <!-- Boutons d'action sur un utilisateur situé à droite des colonnes -->
        <template #menu-right="{ row }">
            <template>
                <b-button @click="addOrUpdateUser(row.Id, row.LastName + ' ' + row.FirstName)" v-b-tooltip.noninteractive.hover :title="$t('Edit')" :variant="'outline-' + Variant"
                    :disabled="!$store.getters['CheckAction']('Write', undefined, Action)"
                    class="btn-circle border-0 p-0">
                    <b-icon icon="pencil-fill" />
                </b-button>

                <!-- Utilisation de la classe invisible à la place de v-if pour conserver le positionnement -->
                <b-button @click="copyToClipboardResetLink(row)" v-b-tooltip.noninteractive.hover :title="$t('PastResetPasswordLink')" :variant="'outline-' + Variant"
                    :disabled="!$store.getters['CheckAction']('Write', undefined, Action)"
                    :class="['btn-circle border-0 p-0', {'invisible': row.SsoAuthorityRef}]">
                    <b-icon icon="link-45deg" />
                </b-button>

                <b-button @click="sendResetEmail(row)" v-b-tooltip.noninteractive.hover :title="$t('SendResetPasswordEmail')" :variant="'outline-' + Variant"
                    :disabled="!$store.getters['CheckAction']('Write', undefined, Action)"
                    :class="['btn-circle border-0 p-0', {'invisible': !(!row.SsoAuthorityRef && row.Email && row.Email !== '') }]">
                    <b-icon icon="envelope-fill" />
                </b-button>
            </template>
        </template>

        <!-- Personnalisation de la colonne 'RegistrationType' -->
        <template #column(RegistrationType)="{ row }">
            <div v-if="row.RegistrationType !== null && !row.IsValidate && row.RegistrationType === RegistrationTypeEnum.Registration">
                {{ $t('Register') }} {{ row.IsExpired ? "(" + $t('Expired') + ")" : "" }}
            </div>
            <div v-else-if="row.RegistrationType !== null && !row.IsValidate && row.RegistrationType === RegistrationTypeEnum.Activation">
                {{ $t('Activate') }}  {{ row.IsExpired ? "(" + $t('Expired') + ")" : "" }}
            </div>
            <div v-else-if="row.RegistrationType !== null && !row.IsValidate && row.RegistrationType === RegistrationTypeEnum.ResetPassword">
                {{ $t('ResetPassword') }} {{ row.IsExpired ? "(" + $t('Expired') + ")" : "" }}
            </div>
            <div v-else></div>
        </template>
    </GenericTable>
</template>

<script lang="ts">
/** Importe les décorateurs */
import Vue from 'vue';
import { Component, InjectReactive, Prop } from 'vue-property-decorator';

/** Types custom importé depuis /types */
import Dictionary from '@/types/Dictionary';
import RegistrationTypeEnum from '@/enums/RegistrationTypeEnum';

/** Accès webAPI */
import axios, { AxiosResponse } from 'axios';
import { API_URL } from '@/App.settings';

/** Fichiers de traductions */
import locales from "./locales/UsersList.json";

/** Types et composants personnalisés employé dans le composant */
import { UserInformation } from './UserInformation';
import GenericTable, { HeaderCustom } from '@/components/commons/Generics/GenericTable.vue';
import ITableHeader from '@/interfaces/ITableHeader';

import { HeaderBoolean, HeaderString, Row, SelectionTypeEnum } from '@/components/commons/Generics/GenericTable.vue';

/** Page de gestion des utilisateurs [Administration] */
@Component({
    components: {
        GenericTable,
	},
    i18n: { messages : locales}
})
export default class UsersList extends Vue {
    /** Pour pouvoir utiliser l'enum WorkingModeEnum dans la section template */
    private RegistrationTypeEnum = RegistrationTypeEnum;
    // Pour avoir accès à l'énumérer dans la partie Vue.
    private selectionTypeEnum = SelectionTypeEnum;       

    /** Variant du menu */
    @InjectReactive( )
    private Variant!: string;

    /** Action utilisée pour la gestion des droits */
    @InjectReactive()
    private Action!: string;

    /** Nombre d'éléments par page */
    @Prop({ default: () => 10})
    public PerPage!: number;

    /** Liste des utilisateurs */
    private userList: UserInformation[] = [];

    /** Liste des colonnes du tableau des utilisateurs */
    private userHeaders: ITableHeader[] = [
        new HeaderString("Name", this.$t('Name').toString(), { HideMenu: false, ShowColumn: true, Sort: true }),
        new HeaderString("InCompany", this.$t('InCompany').toString(), { HideMenu: false, ShowColumn: true, Sort: true }),
        new HeaderString("Email", this.$t('Email').toString(), { HideMenu: false, ShowColumn: true, Sort: true }),
        new HeaderString("TelNumber", this.$t('LandlinePhone').toString(), { HideMenu: false, ShowColumn: true, Sort: true }),
        new HeaderString("Profil", this.$t('Profile').toString(), { HideMenu: false, ShowColumn: true, Sort: true }),
        new HeaderCustom("RegistrationType", this.$t('RegistrationType').toString(), { HideMenu: false, ShowColumn: true, Sort: true }),
        new HeaderBoolean("IsActif", this.$t('Status').toString(), { HideMenu: false, ShowColumn: true, Sort: false }),
    ];

    /** Charge la liste des utilisateurs */
    private async loadUsers(){
        await this.$store.dispatch("ShowLoadingMask", this.$t("LoadingUsers"));

        /** Nettoyage de la liste sans toucher à la référence du tableau */
        this.userList.splice(0, this.userList.length);
        
        const args: Dictionary<any> = {};
        args["Token"] = Vue.cookies.get('access_token') != null ? <string>Vue.cookies.get('access_token') : "";
            
        try{
            const response : AxiosResponse<any,any> = await axios.post(API_URL + "Administration/Users/GetUsers", args)
            if(response && response.data){
                // N'ayant aucun moyen de savoir si le filterTree fait disparaitre le chargement je suis obligé de le faire à plusieur instant.
                await this.$store.dispatch("ShowLoadingMask", this.$t("LoadingUsers"));
                const users: UserInformation[] = response.data; 
                Vue.set(this, 'userList', users);
                // N'ayant aucun moyen de savoir si le filterTree fait disparaitre le chargement je suis obligé de le faire à plusieur instant.
                await this.$store.dispatch("ShowLoadingMask", this.$t("LoadingUsers"));
            }
        }catch(err){
            console.error(err);
            this.$bvToast.toast(this.$t('AnErrorOccured').toString(), { 
                title: this.$t('Error').toString(),
                autoHideDelay: 10000,
                variant: "danger",
                solid: true,
                appendToast: true,
                toaster: "b-toaster-top-center"
            });
        }finally{
            await this.$store.dispatch("HideLoadingMask", {});
        }
    }

    /**
     * Suppression de l'utilisateur désigné
     * @param rows Utilisateur visé
     */
    private async deleteUser(rows: Row[]){
        let message = [];
            /** forge du message de validation sur le modèle de celui de Sandrine */
            message.push(this.$tc('MsgConfirmUserDelete', rows.length).toString());

            for(let row of rows){
                const user = row as UserInformation;
                message.push(this.$createElement('div',
                        [this.$createElement('b-icon', {props: {icon: 'arrow-right'}}),
                            "  " + user.Name + (user.Email ? ' (' + user.Email + ')' : '')]));
            }

            message.push(this.$createElement('br'));
            message.push(this.$createElement('div', [
                this.$createElement('b-icon', {props: {icon: 'exclamation-triangle-fill', variant: "warning"}}),
                "  " + this.$t('MsgConfirmInfoDelete').toString()
            ]));

            this.$bvModal.msgBoxConfirm([this.$createElement('p', message)], {
                title: this.$t('DeleteUser').toString(),
                okVariant: 'danger',
                cancelVariant: 'light',
                okTitle: this.$t('Delete').toString(),
                cancelTitle: this.$t('No').toString(),
                hideHeaderClose: false,
                centered: true
            }).then(async value => {
                if(!value) return;
                this.$store.dispatch("ShowLoadingMask", this.$t('UserDeletion'));

                for (const row of rows) {
                    const user = row as UserInformation;
                    /** Suppression de l'utilisateur */
                    const args: Dictionary<any> = {};
                    args["Token"] = Vue.cookies.get('access_token') != null ? <string>Vue.cookies.get('access_token') : "";
                    args["Id"] = row.Id;

                    // Récupération de la liste des téléphones
                    await axios.post(API_URL + "Administration/Users/Delete", args).then((response) => {
                        this.$bvToast.toast(`${user.Name} (${user.Email})`, { 
                            title: this.$t('UserDeleted').toString(),
                            autoHideDelay: 10000,
                            variant: "success",
                            solid: true,
                            appendToast: true,
                            toaster: "b-toaster-top-center"
                        });
                    }).catch(err =>{
                        console.error(err);
                        this.$bvToast.toast(this.$t('AnErrorOccured').toString(), { 
                            title: this.$t('Error').toString(),
                            autoHideDelay: 10000,
                            variant: "danger",
                            solid: true,
                            appendToast: true,
                            toaster: "b-toaster-top-center"
                        });
                    })
                }

                this.$store.dispatch("HideLoadingMask", {});

                /** Actualisation de liste des utilisateurs pour tenir compte des modifications */
                await this.loadUsers();
            });
    }

    /**
     * Permet d'afficher la modal spécifique soit pour les utilisateurs ou les téléphones de télérelève pour l'ajout/modification
     * @param isPhone Indique si on veut la modal de gestion des téléphones télérelève ou celle des utilisateurs
     * @param id Identifiant soit du téléphone ou de l'utilisateur 
     * @param fullname Nom complet à afficher
     */
    private addOrUpdateUser(id: string|undefined = undefined , fullname: string){
        /** Émission d’un événement pour demander l’édition ou l’ajout d’un utilisateur */
        this.$emit('EditUserEvent', id ? id : -1, fullname)
    }

    /**
     * Envoi un mail de réinitialisation du mot de passe 
     * @param row ligne impacté
     */
    private async sendResetEmail(row: Row){

        this.$store.dispatch("ShowLoadingMask", this.$t("LoadingAction"));

        const user = row as UserInformation;

        if(!user.Email || user.Email === ""){
            this.$bvToast.toast(this.$t('NoEmail').toString(), { 
                title: this.$t('Error').toString(),
                autoHideDelay: 10000,
                variant: "danger",
                solid: true,
                appendToast: true,
                toaster: "b-toaster-top-center"
            });
        }else{
            const args: Dictionary<any> = {
                Token: Vue.cookies.get('access_token') != null ? <string>Vue.cookies.get('access_token') : "",
                Id: row.Id,
                FrontUrl :new URL(this.$router.resolve('Registration').href, window.location.origin),
            };

            // Récupération de la liste des téléphones
            await axios.post(API_URL + "Administration/Users/SendRegistrationMail", args).then((response) => {
                this.$bvToast.toast(this.$t('SendRegistrationLink').toString(), { 
                    title: this.$t('Information').toString(),
                    autoHideDelay: 10000,
                    variant: "info",
                    solid: true,
                    appendToast: true,
                    toaster: "b-toaster-top-center"
                });
            }).catch(err =>{
                console.error(err);
                this.$bvToast.toast(this.$t('AnErrorOccured').toString(), { 
                    title: this.$t('Error').toString(),
                    autoHideDelay: 10000,
                    variant: "danger",
                    solid: true,
                    appendToast: true,
                    toaster: "b-toaster-top-center"
                });
            })
        }

        await this.loadUsers();
        await this.$store.dispatch("HideLoadingMask", {});
    }

    /**
     * Stocke un liens de réinitialisation dans le presse-papier
     * @param row ligne impacté
     */
    private async copyToClipboardResetLink(row: Row){
        this.$store.dispatch("ShowLoadingMask", this.$t("LoadingAction"));

        const args: Dictionary<any> = {
            Token: Vue.cookies.get('access_token') != null ? <string>Vue.cookies.get('access_token') : "",
            Id: row.Id,
            FrontUrl: new URL(this.$router.resolve('Registration').href, window.location.origin) ,
        };

        // Récupération de la liste des téléphones
        await axios.post(API_URL + "Administration/Users/GetRegistrationLink", args).then((response) => {
            this.$bvToast.toast(this.$t('GetRegistrationLink').toString(), { 
                title: this.$t('Information').toString(),
                autoHideDelay: 10000,
                variant: "info",
                solid: true,
                appendToast: true,
                toaster: "b-toaster-top-center"
            });
            // Copy vers le presse papier
            navigator.clipboard.writeText(response.data);
        }).catch(err =>{
            console.error(err);
            this.$bvToast.toast(this.$t('AnErrorOccured').toString(), { 
                title: this.$t('Error').toString(),
                autoHideDelay: 10000,
                variant: "danger",
                solid: true,
                appendToast: true,
                toaster: "b-toaster-top-center"
            });
        })

        await this.loadUsers();
        await this.$store.dispatch("HideLoadingMask", {});
    }

    /** Initialisation du composant */
    async created(){
        /** Chargement de la liste des utilisateurs */
        await this.loadUsers();
    }
}


</script>

<style scoped>
</style>